import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';

import { authorColor } from '../../../../core/utils/author-color';

const AVATAR_COLORS = ['#297CD6'];

@Component({
  selector: 'messaging-avatar',
  templateUrl: './messaging-avatar.component.html',
  styleUrls: ['./messaging-avatar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MessagingAvatarComponent {
  name = input('', { transform: (value: string | null | undefined) => value ?? '' });
  src = input<string | null | undefined>();
  initialsSize = input(2);
  size = input(50, { transform: (value: number | string) => Number(value) });
  colorSeed = input<string | null | undefined>(null);

  private imageError = signal(false);

  constructor() {
    effect(() => {
      this.src();
      this.imageError.set(false);
    });
  }

  showImage = computed(() => !!this.src() && !this.imageError());

  initials = computed(() => this.buildInitials(this.name(), this.initialsSize()));

  initialsStyle = computed(() => ({
    width: `${this.size()}px`,
    height: `${this.size()}px`,
    lineHeight: `${this.size()}px`,
    fontSize: `${this.size() <= 24 ? Math.max(8, this.size() / 2.2) : this.size() / 3}px`,
    backgroundColor: this.colorSeed() ? authorColor(this.colorSeed()) : this.getBackgroundColor(this.initials()),
    color: '#FFF',
  }));

  onImageError(): void {
    this.imageError.set(true);
  }

  private buildInitials(name: string, maxInitials: number): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return '';
    }

    const selected = maxInitials > 0 ? parts.slice(0, maxInitials) : parts;
    return selected.map((part) => part[0].toUpperCase()).join('');
  }

  private getBackgroundColor(text: string): string {
    if (!text) {
      return 'transparent';
    }

    const asciiCodeSum = text
      .split('')
      .map((letter) => letter.charCodeAt(0))
      .reduce((previous, current) => previous + current, 0);

    return AVATAR_COLORS[asciiCodeSum % AVATAR_COLORS.length];
  }
}
